import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'motion/react';

export function AmbientBackground({ className = '' }) {
  const containerRef = useRef(null);
  const reduce = useReducedMotion();
  const frame = useRef(null);
  const target = useRef({ x: 0.5, y: 0.35 });
  const current = useRef({ x: 0.5, y: 0.35 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.style.setProperty('--mx', '50%');
    el.style.setProperty('--my', '35%');

    if (reduce) return;

    function handlePointerMove(e) {
      const rect = el.getBoundingClientRect();
      target.current.x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      target.current.y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    }

    function tick() {
      current.current.x += (target.current.x - current.current.x) * 0.06;
      current.current.y += (target.current.y - current.current.y) * 0.06;
      el.style.setProperty('--mx', `${current.current.x * 100}%`);
      el.style.setProperty('--my', `${current.current.y * 100}%`);
      frame.current = requestAnimationFrame(tick);
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    frame.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [reduce]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)',
          backgroundSize: '30px 30px',
          maskImage: 'radial-gradient(ellipse 90% 70% at 50% 0%, black 25%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 70% at 50% 0%, black 25%, transparent 75%)',
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(720px circle at var(--mx) var(--my), rgba(99,102,241,0.20), transparent 60%)',
        }}
      />
      <motion.div
        className="absolute -top-40 left-[5%] h-[30rem] w-[30rem] rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.25), transparent 70%)' }}
        animate={reduce ? undefined : { x: [0, 70, 0], y: [0, 35, 0] }}
        transition={reduce ? undefined : { duration: 19, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute -bottom-24 right-[0%] h-[24rem] w-[24rem] rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(129,140,248,0.16), transparent 70%)' }}
        animate={reduce ? undefined : { x: [0, -60, 0], y: [0, -30, 0] }}
        transition={reduce ? undefined : { duration: 24, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}
